import React from 'react';
import { Box, Flex, Text, Icon, Tooltip, AbstractButton } from 'pounceV2';
import { useTheme } from '@mui/material';
import dayjs from 'dayjs';
import FlatBadgeV3 from 'Components/badges/FlatBadge/FlatBadgeV3';
import UserAvatarV3 from 'Components/UserAvatar/UserAvatarV3';
import { getActorDisplayName } from 'Helpers/utils';
import HighlightedText from './HighlightedText';
import type { SearchResultNode } from './hooks/useKendraSearch';

// Helper function to get icon type based on aliasType
const getIconForAliasType = (aliasType: string | null | undefined) => {
  if (!aliasType) {
    return 'sparkle' as const;
  }
  const lowerAliasType = aliasType.toLowerCase();
  if (lowerAliasType.includes('alert')) {
    return 'alert-circle' as const;
  }
  if (lowerAliasType.includes('query')) {
    return 'search' as const;
  }
  return 'sparkle' as const;
};

const getAliasTypeLabel = (aliasType: string | null | undefined): string => {
  const iconType = getIconForAliasType(aliasType);
  if (iconType === 'alert-circle') {
    return 'Alert';
  }
  if (iconType === 'search') {
    return 'Search Results';
  }
  return 'Chat';
};

interface KendraSearchResultsProps {
  results: Array<{ node: SearchResultNode }>;
  totalCount?: number;
  hasNextPage?: boolean;
  loading?: boolean;
  onResultClick: (node: SearchResultNode) => void;
  onClearResults: () => void;
  onLoadMore: () => void;
  resultFilter?: (result: { node: SearchResultNode }) => boolean;
  showAliasType?: boolean;
}

const KendraSearchResults = ({
  results,
  totalCount,
  hasNextPage,
  loading,
  onResultClick,
  onClearResults,
  onLoadMore,
  resultFilter,
  showAliasType,
}: KendraSearchResultsProps) => {
  const {
    palette: { componentTokens, tokens },
  } = useTheme();

  const filteredResults = resultFilter ? results.filter(resultFilter) : results;

  const resultItemHoverSx = React.useMemo(
    () => ({
      '&:hover': {
        backgroundColor: componentTokens.selectMenuItems.color.item.background.row.hover,
        borderLeft: '3px solid',
        borderLeftColor: tokens.stroke.accent[1].base,
        paddingLeft: '13px',
      },
    }),
    [componentTokens, tokens],
  );

  if (!filteredResults.length) {
    return null;
  }

  return (
    <Box>
      <Flex alignItems="center" gap={3} mb={2} pt="5px">
        <Text color={tokens.content.text.accent[14][3]} fontWeight="bold" variant="body/sm">
          SEARCH RESULTS ({totalCount?.toLocaleString() ?? filteredResults.length.toLocaleString()})
        </Text>
        <AbstractButton
          onClick={onClearResults}
          color={componentTokens.buttons.colors.link.destructive.text.resting}
          sx={{ '&:hover': { color: componentTokens.buttons.colors.link.destructive.text.hover } }}
          fontSize="small"
        >
          Clear Results
        </AbstractButton>
      </Flex>
      {filteredResults.map(result => (
        <AbstractButton
          key={result.node.streamId}
          py={3}
          px={2}
          display="block"
          width="100%"
          textAlign="left"
          sx={resultItemHoverSx}
          cursor="pointer"
          borderLeft="3px solid"
          borderLeftColor="transparent"
          transition="all 0.2s ease"
          onClick={() => onResultClick(result.node)}
        >
          {result.node.title && (
            <Box mb={2} width="100%">
              <Flex alignItems="center" gap={2} flexWrap="nowrap">
                <Text flexShrink={0}>{result.node.title}</Text>
                {!result.node.private && (
                  <Box flexShrink={0}>
                    <FlatBadgeV3>SHARED</FlatBadgeV3>
                  </Box>
                )}
                {result.node.createdBy && (
                  <>
                    <Text color={tokens.content.assets.neutral[3]} flexShrink={0}>
                      &bull;
                    </Text>
                    <Flex alignItems="center" gap={1} flexShrink={0}>
                      <UserAvatarV3
                        name={getActorDisplayName(result.node.createdBy)}
                        entityId={result.node.createdBy.id}
                      />
                      <Text>{getActorDisplayName(result.node.createdBy)}</Text>
                    </Flex>
                  </>
                )}
                {result.node.createdAt && (
                  <>
                    <Text color={tokens.content.assets.neutral[3]} flexShrink={0}>
                      &bull;
                    </Text>
                    <Tooltip
                      content={dayjs(result.node.createdAt).format('MMMM D, YYYY h:mm A')}
                      placement="top"
                    >
                      <Text flexShrink={0}>{dayjs(result.node.createdAt).fromNow()}</Text>
                    </Tooltip>
                  </>
                )}
                {result.node.userPrompt && (
                  <>
                    <Text color={tokens.content.assets.neutral[3]} flexShrink={0}>
                      &bull;
                    </Text>
                    <Icon
                      type="user"
                      size="small"
                      flexShrink={0}
                      color={componentTokens.selectMenuItems.color.item.assets.row.resting}
                    />
                    <Tooltip
                      content={result.node.userPrompt.length > 64 ? result.node.userPrompt : ''}
                      disableHoverListener={result.node.userPrompt.length <= 64}
                      placement="top"
                    >
                      <Text truncated>
                        {`"${
                          result.node.userPrompt.length > 64
                            ? `${result.node.userPrompt.slice(0, 64)}...`
                            : result.node.userPrompt
                        }"`}
                      </Text>
                    </Tooltip>
                  </>
                )}
                {showAliasType && result.node.aliasType && (
                  <>
                    <Text color={tokens.content.assets.neutral[3]} flexShrink={0}>
                      &bull;
                    </Text>
                    <Tooltip content={getAliasTypeLabel(result.node.aliasType)} placement="top">
                      <Icon
                        type={getIconForAliasType(result.node.aliasType)}
                        size="small"
                        color={tokens.content.assets.neutral.base}
                        flexShrink={0}
                      />
                    </Tooltip>
                  </>
                )}
              </Flex>
            </Box>
          )}
          <Box>
            <Text color={tokens.content.text.accent[14][3]} mb={2}>
              <HighlightedText
                text={result.node.snippetWithHighlights.text}
                highlights={result.node.snippetWithHighlights.highlights}
              />
            </Text>
            {result.node.createdAt && !result.node.title && (
              <Tooltip content={dayjs(result.node.createdAt).format('MMMM D, YYYY h:mm A')}>
                <Flex alignItems="center" gap={1}>
                  <Icon
                    type="calendar"
                    size="x-small"
                    color={tokens.content.text.accent[14].base}
                  />
                  <Text variant="system" color={tokens.content.text.accent[14].base}>
                    {dayjs(result.node.createdAt).format('MMM D, YYYY')}
                  </Text>
                </Flex>
              </Tooltip>
            )}
          </Box>
        </AbstractButton>
      ))}
      {hasNextPage && (
        <Box py={3} textAlign="center">
          <AbstractButton
            onClick={onLoadMore}
            disabled={loading}
            color={componentTokens.buttons.colors.link.primary.text.resting}
            sx={{
              '&:hover': { color: componentTokens.buttons.colors.link.primary.text.hover },
            }}
            fontSize="small"
          >
            {loading ? 'Loading...' : 'Load More'}
          </AbstractButton>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(KendraSearchResults);
